import * as Yup from "yup";
import { Box } from "@mui/system";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { IconHelp } from "@tabler/icons";
import { useEffect, useState } from "react";
import useStyles from "../../../styles/styles";
import { configState } from "../../../recoil/atoms";
import { InputField } from "../../../utils/FormFields";
import { EditEquipmentRequest, Equipment, EquipmentApi } from "../../../api/medical-operations";
import { Button, CircularProgress, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";

type props = {
  handleCloseModal: Function;
  editEquipment?: string;
  isEdit?: Boolean;
};

const EquipmentModal = ({ handleCloseModal, editEquipment, isEdit }: props) => {
  const classes = useStyles();
  const config = useRecoilValue(configState);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [equipment, setEquipment] = useState<Equipment>();

  const addEquipment = (values: any) => {
    const Equipment = new EquipmentApi(config);
    Equipment.addEquipment(values)
      .then((res) => {
        toast.success(res?.data?.Message!);
        handleCloseModal();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const editMedicalEquipment = (values: EditEquipmentRequest) => {
    const Equipment = new EquipmentApi(config);
    Equipment.editEquipment(editEquipment!, values)
      .then((res) => {
        toast.success(res?.data?.Message!);
        handleCloseModal();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleSubmit = (values: EditEquipmentRequest, actions: any) => {
    const editedValues: EditEquipmentRequest = {
      Name: values?.Name,
      Code: values?.Code,
      Price: values?.Price,
      Brand: values?.Brand,
      ExpiryDate: values?.ExpiryDate,
    };
    isEdit ? editMedicalEquipment(editedValues) : addEquipment(values);
  };

  const getEquipment = () => {
    const Equipment = new EquipmentApi(config);
    Equipment.getEquipmentById(editEquipment!)
      .then((res) => {
        setIsLoading(false);
        setEquipment(res?.data);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hint = () => (
    <Tooltip title="Equipment code must start with E">
      <IconButton>
        <IconHelp />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {!isLoading ? (
        <Formik
          initialValues={
            editEquipment
              ? equipment!
              : { Name: undefined, Code: undefined, Price: undefined, ExpiryDate: undefined, Brand: undefined }
          }
          enableReinitialize={true}
          validationSchema={Yup.object().shape({
            Name: Yup.string()
              .trim()
              .matches(/[a-zA-Z ']+$/, "Incorrect format")
              .min(0, "Cannot be empty")
              .max(50, "Must be 50 characters or less")
              .required("Required"),
            Brand: Yup.string()
              .trim()
              .matches(/[a-zA-Z ']+$/, "Incorrect format")
              .min(0, "Cannot be empty")
              .max(20, "Must be 20 characters or less")
              .required("Required"),
            Code: Yup.string()
              .trim()
              .matches(/^E[a-zA-Z0-9']+$/, "Code must start with E")
              .min(2, "Invalid Code")
              .max(8, "Must be 8 characters or less")
              .required("Required"),
            Price: Yup.number().required("Required").min(0,"Price cannot be negative"),            
          })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <Typography variant="h5" gutterBottom sx={{ p: 0 }}>
                {isEdit ? "Edit Equipment" : "Add Equipment"}
              </Typography>
              <Divider />
              <Grid
                container
                rowSpacing={4}
                spacing={2}
                sx={{
                  mt: 2,
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                <Grid item xs={12} sm={10}>
                  <InputField name="Code" label="Code" fullWidth suffix={hint()} />
                </Grid>
                <Grid container />
                <Grid item xs={12} sm={10}>
                  <InputField name="Name" label="Name" fullWidth />
                </Grid>
                <Grid container />
                <Grid item xs={12} sm={10}>
                  <InputField name="Brand" label="Brand" fullWidth />
                </Grid>
                <Grid container />
                <Grid item xs={12} sm={10}>
                  <InputField name="Price" label="Price" fullWidth type="number" suffix="$" />
                </Grid>                               
              </Grid>
              <Grid container className={classes.buttons} sx={{ mt: 6 }}>
                <Grid item className={classes.wrapper}>
                  <Button
                    onClick={() => handleCloseModal()}
                    variant="contained"
                    color="error"
                    className={classes.button}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="secondary" className={classes.button}>
                    {isEdit ? "Edit Equipment" : "Add Equipment"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress
            color="secondary"
            size={36}
            sx={{
              ml: { xs: 0, xl: 3 },
              mt: 2,
            }}
          />
        </Box>
      )}
    </>
  );
};

export default EquipmentModal;
